/**
 * ⟦boton-de-descarga⟧ `R4-UI0` · **EL BOTÓN QUE DICE «Descargando…» Y AVISA POR
 * `onError`.**
 *
 * R4-F2 (PRD_R4.md:1159): «Está habilitado siempre, salvo mientras se arma un
 * archivo: entonces queda deshabilitado y muestra un indicador de carga con el
 * texto «Descargando…» en lugar de «Descargar CSV»». R4-F4 (PRD_R4.md:2015):
 * «Tocarlo otra vez no hace nada».
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ EL AVISO DE ERROR NO SE PINTA ADENTRO. SALE POR `onError`.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * La salida cómoda es renderizar «No se pudo descargar el CSV. Intenta de
 * nuevo.» adentro del botón «porque así es reutilizable». El PRD lo pone
 * «Debajo del encabezado» (PRD:1269), así que adentro queda fuera de lugar en
 * las tres pantallas. Por eso el error se ENTREGA: quien pinta el aviso es la
 * pantalla, que sabe dónde va.
 *
 * ⚠️ **El rótulo llega por parámetro.** Las tres pantallas dicen «Descargar
 * CSV», «Exportar CSV de resultados» y «Exportar CSV de errores»: fijar
 * cualquiera adentro deja a las otras dos sin poder decir el suyo y la copia
 * diverge en la primera corrección.
 */

import { useState } from "react"

interface Props {
  /** «Descargar CSV», «Exportar CSV de resultados», «Exportar CSV de errores»… */
  readonly rotulo: string
  /** Con la descarga que falla, la pantalla pinta su aviso donde el PRD lo pone. */
  readonly onError?: (error: unknown) => void
  readonly accion: () => void | Promise<void>
}

export function BotonDeDescarga({ rotulo, onError, accion }: Props) {
  const [cargando, setCargando] = useState(false)

  const ejecutar = async (): Promise<void> => {
    /*
     * ⛔ La guarda de `cargando` es la que cumple «Tocarlo otra vez no hace
     * nada»: un segundo toque mientras el primero corre no dispara otro pedido
     * (y por lo tanto no baja dos archivos con dos filas de auditoría).
     */
    if (cargando) return
    setCargando(true)
    try {
      /*
       * ⛔ `Promise.resolve` y no `await accion()` a secas: un doble de examen
       * que devuelve `void` rompería el `.finally()` y el botón nunca volvería a
       * su rótulo.
       */
      await Promise.resolve(accion())
    } catch (error) {
      onError?.(error)
    } finally {
      setCargando(false)
    }
  }

  return (
    <button type="button" onClick={ejecutar} disabled={cargando}>
      {cargando ? "Descargando…" : rotulo}
    </button>
  )
}
